import { faClose, faCircleInfo, faLink, faStore } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { Permissions } from "../../../Enum/PermissionsType"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import type { StoreGroupSaveRequest } from "../../../hooks/StoreGroups/useSaveStoreGroup"
import type { StoreGroup } from "../../../Objects/StoreGroups/StoreGroup"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { normalizeSlug } from "../../../utils/slug"
import GetSpace from "../../../utils/space"

interface StoreGroupPopupProps {
  group?: StoreGroup | null
  onClose: () => void
  onSave: (request: StoreGroupSaveRequest & { isActive: boolean }) => Promise<boolean>
  onDelete?: () => Promise<boolean>
}

export const StoreGroupPopup: React.FC<StoreGroupPopupProps> = ({ group, onClose, onSave, onDelete }) => {
  const { t } = useTranslation(["company", "helpPopup"])

  const { setNewConfirmation } = useConfirmation()

  const [name, setName] = useState("")

  const [description, setDescription] = useState("")

  const [slug, setSlug] = useState("")

  const [isSlugAutomatic, setIsSlugAutomatic] = useState(!group)

  const [isActive, setIsActive] = useState(true)

  const [icon, setIcon] = useState<File | null>(null)

  const [saving, setSaving] = useState(false)

  const isEditing = Boolean(group)

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  useEffect(() => {
    setName(group?.name ?? "")

    setDescription(group?.description ?? "")

    setSlug(group?.slug ?? "")

    setIsSlugAutomatic(!group)

    setIsActive(group?.isActive ?? true)

    setIcon(null)
  }, [group])

  const handleNameChange = (value: string) => {
    setName(value)

    if (isSlugAutomatic) {
      setSlug(normalizeSlug(value))
    }
  }

  const handleSlugChange = (value: string) => {
    setIsSlugAutomatic(false)
    setSlug(normalizeSlug(value))
  }

  const handleSubmit = async () => {
    const finalSlug = slug || normalizeSlug(name)

    if (!name.trim() || !finalSlug || saving || canEdite) {
      return
    }

    setSaving(true)

    try {
      const saved = await onSave({
        name,
        description,
        slug: finalSlug,
        isActive,
        icon,
      })

      if (saved) {
        onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete || saving || canEdite) {
      return
    }

    setNewConfirmation(t("company:store_groups.delete_group"), t("company:store_groups.delete_confirm"), async () => {
      setSaving(true)

      try {
        const deleted = await onDelete()

        if (deleted) {
          onClose()
        }
      } finally {
        setSaving(false)
      }
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={isEditing ? t("company:store_groups.edit_title") : t("company:store_groups.create_title")}
              icon={faStore}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.store-groups"
                    title={t("helpPopup:global.storeGroups.title")}
                    description={t("helpPopup:global.storeGroups.description")}
                    bullets={t("helpPopup:global.storeGroups.bullets", { returnObjects: true }) as string[]}
                    steps={t("helpPopup:global.storeGroups.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleSubmit()
              }}
            >
              <InputPhotoWithContainer
                iconButton
                initialPhoto={group?.iconUrl}
                handleFileSelected={setIcon}
                imageProfile="company"
                color={THEME.colors.buttonBGPlusColor}
                disable={canEdite}
              />

              <div className={defaultStyle.divisionBar}></div>

              <InputField
                type="text"
                value={name}
                placeholder={t("company:store_groups.name")}
                icon={faCircleInfo}
                maxLength={100}
                required
                disable={canEdite}
                onChange={(event) => handleNameChange(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.input.name"
              />

              <GetSpace top={10} />

              <InputField
                type="text"
                value={slug}
                placeholder={t("company:store_groups.slug")}
                icon={faLink}
                maxLength={100}
                required
                disable={canEdite}
                onChange={(event) => handleSlugChange(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.input.slug"
              />

              <GetSpace top={10} />

              <InputField
                type="text"
                value={description}
                placeholder={t("company:store_groups.public_description")}
                icon={faCircleInfo}
                maxLength={300}
                disable={canEdite}
                onChange={(event) => setDescription(event.target.value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.input.publicDescription"
              />

              {isEditing && (
                <>
                  <GetSpace top={10} />

                  <SimpleToggle
                    title={t("company:store_groups.is_active")}
                    value={isActive}
                    disabled={saving || canEdite}
                    onChange={setIsActive}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.toggle.isActive"
                  />
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                {isEditing && onDelete && (
                  <CanView permission={Permissions.CanEditeCompany}>
                    <>
                      <Button
                        label={t("company:store_groups.delete_group")}
                        typeButton="button"
                        disabled={saving}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={handleDelete}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.button.deleteGroup"
                      />
                      <div style={{ width: "8px" }} />
                    </>
                  </CanView>
                )}

                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    label={t("company:store_groups.save")}
                    typeButton="submit"
                    disabled={saving}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupPopup.button.save"
                  />
                </CanView>
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
